import { Navigate, Outlet, useLocation } from "react-router";
import { useAuthStore } from "./auth-store";

/**
 * Route guard component that wraps protected routes.
 * - Unauthenticated users → /sign-in
 * - Authenticated but onboarding not completed → /onboarding (FR-044)
 * - Authenticated and onboarded → render children
 *
 * Feature 034: Added onboarding redirect logic
 */
export function AuthGuard(): React.ReactElement {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const location = useLocation();

  if (!isAuthenticated) {
    // Preserve the attempted URL as redirect param
    const redirectPath = location.pathname + location.search;
    return <Navigate to={`/sign-in?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  // Feature 034: Redirect un-onboarded users to onboarding flow
  if (user && !user.onboardingCompletedAt) {
    return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
}

/**
 * Guard for onboarding routes.
 * - Unauthenticated → /sign-in
 * - Authenticated AND already onboarded → /home (FR-044: completed users bypass)
 * - Authenticated AND not onboarded → render onboarding children
 *
 * Feature 034: Dedicated onboarding route guard
 */
export function OnboardingGuard(): React.ReactElement {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const location = useLocation();

  if (!isAuthenticated) {
    const redirectPath = location.pathname + location.search;
    return <Navigate to={`/sign-in?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  // Already onboarded → bypass to home
  if (user?.onboardingCompletedAt) {
    return <Navigate to="/home" replace />;
  }

  return <Outlet />;
}
